import { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { activeClientState, dataVersionState } from '../../state.js';
import { api } from '../../api.js';
import { Card, EmptyState } from '../Card.js';
import { ConfirmDialog } from '../ConfirmDialog.js';
import { ErrorBanner } from '../FormBits.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.js';
import { Switch } from '../ui/switch.js';
import { Table, THeadRow, TH, TD, TR, Ltr } from '../ui/table.js';
import { fmtNum } from '../../utils.js';
import { FUND_CURRENCIES, FUND_CURRENCY_LABELS_HE, type FundCurrency } from '@agio/core/domain';

/**
 * The client's portfolios, and the tools to fix them.
 *
 * A fund's name arrives from two places that can disagree — the master
 * workbook's sheet title and a monthly statement's filename — and when they
 * disagree the client quietly ends up with two portfolios and a balance counted
 * twice. A plain list makes that invisible, so this one says it out loud and
 * offers the repair: rename one onto the other and they merge.
 */
interface AdminFund {
  fund_id: string;
  fund_name: string;
  is_visible: boolean;
  is_old: boolean;
  currency: FundCurrency;
  snapshots: number;
  last_date: string | null;
  last_total: number | null;
  sources: string[];
  possible_duplicate_of: string | null;
}

/** Hebrew counts one differently from many: "תמונת מצב אחת", not "1 תמונות מצב". */
const snapshots = (n: number) => (n === 1 ? 'תמונת מצב אחת' : `${n} תמונות מצב`);

export function FundsCard() {
  const bump = useSetRecoilState(dataVersionState);
  const clientId = useRecoilValue(activeClientState) ?? '';
  const [funds, setFunds] = useState<AdminFund[] | null>(null);
  const [renaming, setRenaming] = useState<AdminFund | null>(null);
  const [doomed, setDoomed] = useState<AdminFund | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = async (id: string) => setFunds(await api.get<AdminFund[]>(`/admin/clients/${id}/funds`));

  useEffect(() => {
    // A note like "X was renamed" belongs to the client it happened to.
    setFunds(null);
    setError(null);
    setNote(null);
    if (!clientId) return;
    let live = true;
    api.get<AdminFund[]>(`/admin/clients/${clientId}/funds`)
      .then((f) => live && setFunds(f))
      .catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [clientId]);

  const act = async (fn: () => Promise<string>) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      setNote(await fn());
      await load(clientId);
      bump((v) => v + 1);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const duplicates = (funds ?? []).filter((f) => f.possible_duplicate_of);

  return (
    <Card>
      {renaming && (
        <RenameDialog
          fund={renaming}
          existing={(funds ?? []).map((f) => f.fund_name)}
          busy={busy}
          onCancel={() => setRenaming(null)}
          onConfirm={(name) => {
            const f = renaming;
            setRenaming(null);
            void act(async () => {
              const r = await api.patch<{ merged: boolean; moved: { holdings: number } }>(
                `/admin/funds/${encodeURIComponent(f.fund_id)}`, { fundName: name });
              return r.merged
                ? `"${f.fund_name}" אוחדה לתוך "${name}" — ${snapshots(r.moved.holdings)} הועברו.`
                : `"${f.fund_name}" שונתה ל"${name}".`;
            });
          }}
        />
      )}

      <ConfirmDialog
        open={!!doomed}
        title="למחוק את התיק?"
        confirmLabel="מחק"
        onConfirm={() => {
          const f = doomed!;
          setDoomed(null);
          void act(async () => {
            await api.del(`/admin/funds/${encodeURIComponent(f.fund_id)}`);
            return `"${f.fund_name}" נמחק.`;
          });
        }}
        onCancel={() => setDoomed(null)}
      >
        <p>
          <span className="font-semibold">{doomed?.fund_name}</span> יימחק, יחד עם{' '}
          {snapshots(doomed?.snapshots ?? 0)}, ניירות הערך והתשואות שנגזרו מהן.
        </p>
        <p className="mt-2 text-muted-foreground">
          אם התיק הזה הוא כפילות, עדיף לשנות את שמו לשם התיק הנכון — כך הנתונים יאוחדו במקום
          להימחק. הפעולה אינה הפיכה.
        </p>
      </ConfirmDialog>

      <div className="grid gap-4">
        {duplicates.length > 0 && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <p className="font-semibold">ייתכן שאותו תיק מופיע פעמיים</p>
            <p className="mt-1">
              {duplicates.map((f) => `"${f.fund_name}"`).join(' ו-')} נראים כמו אותו תיק. אם כן,
              היתרה של הלקוח נספרת פעמיים. שנו את שם אחד מהם לשם השני — הנתונים יאוחדו לתיק אחד.
            </p>
          </div>
        )}

        {note && (
          <div className="rounded-md border border-success/40 bg-success/10 px-4 py-2 text-sm text-success">{note}</div>
        )}

        {!clientId && <EmptyState label="בחרו לקוח למעלה" />}
        {clientId && funds?.length === 0 && <EmptyState label="אין תיקים ללקוח הזה" />}

        {funds && funds.length > 0 && (
          <Table minWidth="56rem">
            <THeadRow>
              <TH>תיק</TH>
              <TH align="end">תמונות מצב</TH>
              <TH>עדכון אחרון</TH>
              <TH align="end">יתרה</TH>
              <TH>מטבע</TH>
              <TH>מקור</TH>
              <TH>מוצג ללקוח</TH>
              <TH align="end" />
            </THeadRow>
            <tbody>
              {funds.map((f) => (
                <TR key={f.fund_id}>
                  <TD className="font-medium">
                    {f.fund_name}
                    {f.possible_duplicate_of && (
                      <span className="mt-0.5 block text-xs font-normal text-destructive">
                        אולי אותו תיק כמו "{f.possible_duplicate_of}"
                      </span>
                    )}
                  </TD>
                  <TD align="end" className="tabular-nums text-muted-foreground">{f.snapshots}</TD>
                  <TD><Ltr>{f.last_date ?? 'ללא תאריך'}</Ltr></TD>
                  <TD align="end" className="tabular-nums">
                    {f.last_total == null ? '—' : fmtNum(f.last_total)}
                  </TD>
                  <TD>
                    {/* Display only: the portal writes this fund's amounts with this sign. Nothing is converted. */}
                    <Select
                      dir="rtl"
                      value={f.currency}
                      disabled={busy}
                      onValueChange={(v: string) => void act(async () => {
                        await api.patch(`/admin/funds/${encodeURIComponent(f.fund_id)}`, { currency: v });
                        return `"${f.fund_name}" יוצג ב${FUND_CURRENCY_LABELS_HE[v as FundCurrency]}.`;
                      })}
                    >
                      <SelectTrigger className="h-8 min-w-28 bg-popover text-xs" aria-label={`מטבע ${f.fund_name}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FUND_CURRENCIES.map((c) => (
                          <SelectItem key={c} value={c}>{FUND_CURRENCY_LABELS_HE[c]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TD>
                  <TD className="max-w-[13rem] truncate text-xs text-muted-foreground" title={f.sources.join(', ')}>
                    {f.sources.join(', ') || '—'}
                  </TD>
                  <TD>
                    <Switch
                      checked={f.is_visible}
                      disabled={busy}
                      aria-label={`הצגת ${f.fund_name} ללקוח`}
                      onChange={(v: boolean) => void act(async () => {
                        await api.patch(`/admin/funds/${encodeURIComponent(f.fund_id)}`, { isVisible: v });
                        return `"${f.fund_name}" ${v ? 'מוצג' : 'מוסתר'} ללקוח.`;
                      })}
                    />
                  </TD>
                  <TD align="end">
                    <span className="flex justify-end gap-1.5">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setRenaming(f)}
                        className="rounded-md border border-border px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-50"
                      >
                        שינוי שם
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setDoomed(f)}
                        className="rounded-md border border-destructive/40 px-2 py-0.5 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
                      >
                        מחיקה
                      </button>
                    </span>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <ErrorBanner error={error} title="הפעולה נכשלה" />
    </Card>
  );
}

/** Renaming onto an existing name merges, so the dialog says so before it happens. */
function RenameDialog({
  fund,
  existing,
  busy,
  onCancel,
  onConfirm,
}: {
  fund: AdminFund;
  existing: string[];
  busy: boolean;
  onCancel: () => void;
  onConfirm: (name: string) => void;
}) {
  const [name, setName] = useState(fund.fund_name);
  const trimmed = name.trim();
  const merges = trimmed !== fund.fund_name && existing.includes(trimmed);

  return (
    <ConfirmDialog
      open
      title={merges ? 'לאחד את התיקים?' : 'שינוי שם התיק'}
      confirmLabel={merges ? 'אחד תיקים' : 'שמור'}
      onConfirm={() => trimmed && onConfirm(trimmed)}
      onCancel={onCancel}
    >
      <label className="block text-sm">
        <span className="mb-1 block text-muted-foreground">שם התיק</span>
        <input
          value={name}
          disabled={busy}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-input bg-popover px-3 py-2"
          aria-label="שם התיק"
        />
      </label>
      {merges ? (
        <p className="mt-3 text-warning">
          כבר קיים תיק בשם "{trimmed}". {snapshots(fund.snapshots)} של "{fund.fund_name}" יועברו
          אליו והתיק הכפול ייעלם — זו הדרך לתקן תיק שנקלט פעמיים בשני שמות.
        </p>
      ) : (
        <p className="mt-3 text-muted-foreground">
          השם משמש לזיהוי התיק בכל הדוחות. כדי שסטייטמנט חודשי ייקלט לתיק הזה, שם קובץ ה-PDF
          צריך להיות זהה לשם הזה.
        </p>
      )}
    </ConfirmDialog>
  );
}
